/**
 * usePackagingLineState Hook
 *
 * Factorizes packaging change logic shared across ship, receive, and edit modes.
 * @see Sprint 22 - Transferts par Packaging
 */

import { useCallback } from 'react';
import type { InventoryPackagingOption } from '@bstock/shared';

const BASE_UNIT_VALUE = '__base_unit__';

// ---------------------------------------------------------------------------
// Record-based hook (ship / receive modes)
// ---------------------------------------------------------------------------

interface RecordLineShape {
  packagingId: string | null;
  packagingQty: string;
  conversionFactor: number | null;
  qty: number;
}

/**
 * Generic hook for packaging changes on a Record<lineId, T> state.
 * Used by shipping and receiving modes where each line is stored in a Record
 * keyed by its transfer-line id.
 */
export function usePackagingLineState<T extends RecordLineShape>(
  setState: React.Dispatch<React.SetStateAction<Record<string, T>>>,
  getOptions: (lineId: string) => InventoryPackagingOption[],
) {
  const handlePackagingChange = useCallback(
    (lineId: string, value: string) => {
      const options = getOptions(lineId);

      setState((prev) => {
        const current = prev[lineId];
        if (!current) return prev;

        if (value === BASE_UNIT_VALUE || value === '') {
          return {
            ...prev,
            [lineId]: {
              ...current,
              packagingId: null,
              conversionFactor: null,
              packagingQty: String(current.qty),
            },
          };
        }

        const option = options.find((o) => o.packagingId === value);
        if (!option) return prev;

        return {
          ...prev,
          [lineId]: {
            ...current,
            packagingId: value,
            conversionFactor: option.conversionFactor,
            packagingQty: '1',
            qty: option.conversionFactor,
          },
        };
      });
    },
    [setState, getOptions],
  );

  const handlePackagingQtyChange = useCallback(
    (lineId: string, qtyStr: string) => {
      setState((prev) => {
        const current = prev[lineId];
        if (!current) return prev;
        const qty = parseFloat(qtyStr) || 0;
        return {
          ...prev,
          [lineId]: {
            ...current,
            packagingQty: qtyStr,
            qty: current.conversionFactor ? qty * current.conversionFactor : qty,
          },
        };
      });
    },
    [setState],
  );

  return { handlePackagingChange, handlePackagingQtyChange };
}

// ---------------------------------------------------------------------------
// Array-based hook (edit mode)
// ---------------------------------------------------------------------------

interface EditLineShape {
  id: string;
  packagingId: string | null;
  packagingQty: string;
  conversionFactor: number | null;
  quantity: number;
  packagingOptions: InventoryPackagingOption[];
}

/**
 * Hook for packaging changes on an array-based edit-lines state.
 * Used by edit mode where lines are stored as EditLineState[].
 */
export function useEditLinePackaging<T extends EditLineShape>(
  setLines: React.Dispatch<React.SetStateAction<T[]>>,
) {
  const handlePackagingChange = useCallback(
    (lineId: string, value: string) => {
      setLines((prev) =>
        prev.map((l) => {
          if (l.id !== lineId) return l;

          if (value === BASE_UNIT_VALUE || value === '') {
            return {
              ...l,
              packagingId: null,
              conversionFactor: null,
              packagingQty: String(l.quantity),
            };
          }

          const option = l.packagingOptions.find((o) => o.packagingId === value);
          if (!option) return l;

          return {
            ...l,
            packagingId: value,
            conversionFactor: option.conversionFactor,
            packagingQty: '1',
            quantity: option.conversionFactor,
          };
        }),
      );
    },
    [setLines],
  );

  const handlePackagingQtyChange = useCallback(
    (lineId: string, qtyStr: string) => {
      setLines((prev) =>
        prev.map((l) => {
          if (l.id !== lineId) return l;
          const qty = parseFloat(qtyStr) || 0;
          return {
            ...l,
            packagingQty: qtyStr,
            quantity: l.conversionFactor ? qty * l.conversionFactor : qty,
          };
        }),
      );
    },
    [setLines],
  );

  return { handlePackagingChange, handlePackagingQtyChange };
}
